import { Context, Dispatch, ReactNode } from 'react';
import { ActionMap, GoMotoFunction } from '../../API/GoMoto.types';
import { Kiosk } from '../Kiosk/kiosk.types';

/*
 * #region Constants
 * export const AppApiRootOptions = {
 *     SB: 'https://service-sb.gomoto.reyrey.net',
 *     QA: 'https://service-qa.gomoto.reyrey.net',
 *     LocalHost: 'http://localhost:5000'
 * } as const;
 */

/*
 * export const AppApiUsernames = {
 *     SB: 'Sandbox',
 *     QA: 'QA'
 * } as const;
 */

/*
 * export const AppApiKeys = {
 *     SB: '',
 *     QA: ''
 * };
 * #endregion
 */

// #region Setup
/**
 * Development context state object. This holds all the settings we use while in the development
 * environment.
 */
export interface DevelopmentContextState {
    /**
     * When true, we are in the development environment and the settings can be
     * used to enable and disable functionality in the Kiosk application.
     */
    isDev: boolean;
    /**
     * When true, the kiosk timeout is disabled.
     */
    isTimeoutDisabled: boolean;
    /**
     * The key for the current kiosk. When changed, it will reload
     * the application with the new kiosk entered.
     */
    kioskKey?: Kiosk['key'];
    /**
     * When true, hide the keyboard from displaying.
     */
    hideKeyboard: boolean;
    /*
     * appApiRoot?: typeof AppApiRootOptions[keyof typeof AppApiRootOptions];
     * appApiUsername?: typeof AppApiUsernames[keyof typeof AppApiUsernames];
     * appApiKey?: typeof AppApiKeys[keyof typeof AppApiKeys];
     */
}

/**
 * The type for the dispatch provider as well as the dispatch function itself.
 */
export type DevelopmentDispatchState = Dispatch<DevelopmentActions>;

/**
 * The context type for the admin state context. It can be undefined so that
 * we can verify anything attempting to consume the context is a child of the context.
 */
export type DevelopmentState = DevelopmentContextState | undefined;

/**
 * The context type for the admin dispatch context. It can be undefined so that
 * we can verify anything attempting to use the dispatch is a child of the context.
 */
export type DevelopmentDispatch = DevelopmentDispatchState | undefined;

/**
 * The admin state context type that is returned after the creation of the state context.
 */
export type DevelopmentStateContextType = Context<DevelopmentState>;

/**
 * The admin dispatch context type that is returned after the creation of the dispatch context.
 */
export type DevelopmentDispatchContextType = Context<DevelopmentDispatch>;
// #endregion

// #region Reducer
/**
 * The admin dispatch types used by our {@link ActionMap} type to distinguish what payload the
 * dispatch action will require.
 */
export enum DevelopmentDispatchTypes {
    /**
     * Dispatch to set whether we are in the development environment or not when the application
     * first loads.
     */
    SetIsDev = 'SET_IS_DEV',
    /**
     * Dispatch to set whether the timeout should be disabled or not.
     */
    SetTimeoutDisabled = 'SET_TIMEOUT_DISABLED',
    /**
     * Dispatch to set the kiosk key for the kiosk that should be being used.
     */
    SetKiosk = 'SET_KIOSK',
    /**
     * Dispatch to set whether to hide or show the keyboard for Electron
     */
    SetHideKeyboard = 'SET_HIDE_KEYBOARD',
    /**
     * Dispatch to set what App API Root to use. i.e. SB, QA, or LocalHost
     */
    // SetAppApiRoot = 'SET_APP_API_ROOT'
}

/**
 * An object mapping each dispatch type to the payload required for that dispatch action.
 */
export interface DevelopmentPayload {
    [DevelopmentDispatchTypes.SetIsDev]: boolean;
    [DevelopmentDispatchTypes.SetTimeoutDisabled]: boolean;
    [DevelopmentDispatchTypes.SetKiosk]: Kiosk['key'];
    [DevelopmentDispatchTypes.SetHideKeyboard]: boolean;
    // [DevelopmentDispatchTypes.SetAppApiRoot]: keyof typeof AppApiRootOptions;
}

/**
 * The union type of dispatch actions that results from using {@link ActionMap}.
 */
export type DevelopmentActions = ActionMap<DevelopmentPayload>[keyof ActionMap<DevelopmentPayload>];

/**
 * The admin reducer function type definition. Primarily used by any `useReducer`
 * that uses the function or the function itself.
 */
export type DevelopmentReducerFunction = GoMotoFunction<
    [DevelopmentContextState, DevelopmentActions],
    DevelopmentContextState
>;
// #endregion

// #region Provider
/**
 * The object types for the props of the admin provider component.
 */
export interface DevelopmentProviderProps {
    /**
     * Any element that will render as a child of the admin context.
     */
    children?: ReactNode;
}
// #endregion

// #region Hooks
/**
 * The function type for the `useAdminState` hook. Takes no arguments and returns the current admin state.
 */
export type DevelopmentStateHook = GoMotoFunction<
    [void],
    DevelopmentContextState
>;

/**
 * The function type for the `useAdminDispatch` hook. Takes no arguments and returns a dispatch function for the admin context.
 */
export type DevelopmentDispatchHook = GoMotoFunction<
    [void],
    DevelopmentDispatchState
>;
// #endregion